import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { plansAPI, progressAPI, scoresAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import NotificationToggle from '../../components/NotificationToggle';
import WelcomeVideoCard from '../../components/WelcomeVideoCard';
import WeeklyChallengeCard from '../../components/WeeklyChallengeCard';
import { motion } from 'framer-motion';
import { Flame, Play, CheckCircle2, ChevronRight, Clock, Zap, Calendar, AlertCircle, Brain, Apple, Award, Star, Eye } from 'lucide-react';
import ParentDashboard from './ParentDashboard';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [todayScore, setTodayScore] = useState(null);
  const [parentView, setParentView] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [planRes, statsRes, progRes, badgesRes, scoreRes] = await Promise.all([
          plansAPI.current(),
          progressAPI.stats(),
          progressAPI.weekly(),
          scoresAPI.badges(),
          scoresAPI.daily(),
        ]);
        setPlan(planRes.data);
        setStats(statsRes.data);
        setProgress(progRes.data || []);
        setBadges(badgesRes.data || []);
        setTodayScore(scoreRes.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const completedDrillIds = new Set(progress.map(p => p.drill_id));
  const isWeekend = plan?.is_weekend;

  // Derive player level from birth_year (matches backend get_player_level)
  const playerLevel = (() => {
    const by = user?.profile?.birth_year;
    if (!by) return null;
    const age = new Date().getFullYear() - by;
    if (age <= 10) return '10U';
    if (age <= 12) return '12U';
    if (age <= 14) return '14U';
    return '15+';
  })();
  // Find the day to feature: earliest unlocked day with incomplete drills (so fresh signups catch up in order)
  const sortedDays = (plan?.days || []).slice().sort((a, b) => a.day_number - b.day_number);
  const nextDay = sortedDays.find(d => {
    if (!d.unlocked) return false;
    const dayDrills = d.drills || [];
    if (dayDrills.length === 0) return false;
    return !dayDrills.every(dr => completedDrillIds.has(dr.id));
  });
  const todayDay = nextDay || sortedDays.find(d => d.is_today);
  const isCatchUp = todayDay && !todayDay.is_today;
  const missedDays = sortedDays.filter(d => {
    if (!d.unlocked || d.is_today || d.day_number === todayDay?.day_number) return false;
    const dayDrills = d.drills || [];
    if (dayDrills.length === 0) return false;
    return !dayDrills.every(dr => completedDrillIds.has(dr.id));
  });

  return (
    <Layout>
      <SubscriptionGate feature="training">
        <div className="px-4 py-6 space-y-5">
          {/* Persistent nudge: player must set birth year so drills match their tier */}
          {!user?.profile?.birth_year && (
            <motion.button
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate('/profile')}
              className="w-full text-left bg-[#FFF7ED] border border-[#FB923C] rounded-xl p-4 flex items-center gap-3 hover:bg-[#FFEDD5] transition-colors"
              data-testid="birth-year-nudge-banner"
            >
              <AlertCircle size={20} className="text-[#EA580C] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#9A3412]">Set your birth year</p>
                <p className="text-[10px] text-[#9A3412]/80 mt-0.5">So your training shows the right drills for your age (10U / 12U / 14U / 15+). Tap to fix in 5 seconds.</p>
              </div>
              <ChevronRight size={16} className="text-[#EA580C] shrink-0" />
            </motion.button>
          )}

          {/* Persistent nudge: player needs to add phone + opt in to SMS */}
          {(!user?.profile?.phone || !user?.profile?.sms_opt_in) && (
            <motion.button
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate('/profile')}
              className="w-full text-left bg-[#EFF6FF] border border-[#60A5FA] rounded-xl p-4 flex items-center gap-3 hover:bg-[#DBEAFE] transition-colors"
              data-testid="sms-optin-nudge-banner"
            >
              <AlertCircle size={20} className="text-[#2563EB] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#1E40AF]">
                  {!user?.profile?.phone ? 'Add your phone number' : 'Turn on SMS updates'}
                </p>
                <p className="text-[10px] text-[#1E40AF]/80 mt-0.5">Get coach alerts, weekly challenge drops &amp; feedback by text so you don't miss anything. Reply STOP anytime.</p>
              </div>
              <ChevronRight size={16} className="text-[#2563EB] shrink-0" />
            </motion.button>
          )}

          {/* Greeting + Streak */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
            <div>
              <p className="text-[#94A3B8] text-xs font-bold uppercase tracking-widest">
                {plan?.player_week ? `Week ${plan.player_week} of your program` : 'Welcome back'}
              </p>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight mt-1 text-[#0A2242]" data-testid="dashboard-greeting">
                Hey {user?.name?.split(' ')[0] || 'Player'}!
              </h2>
              {playerLevel && (
                <button
                  onClick={() => navigate('/profile')}
                  className="mt-1.5 inline-flex items-center gap-1.5 bg-[#2563EB]/10 hover:bg-[#2563EB]/15 border border-[#2563EB]/25 text-[#2563EB] rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest transition-colors"
                  title="Your training is curated for your level. Tap to edit your birth year."
                  data-testid="player-level-chip"
                >
                  <Star size={9} strokeWidth={2.5} />
                  Level: {playerLevel}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setParentView(!parentView)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all ${
                  parentView
                    ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30'
                    : 'bg-white border border-[#E2E8F0] text-[#94A3B8] shadow-sm'
                }`}
                data-testid="parent-view-toggle"
              >
                <Eye size={12} /> Parent
              </button>
              {!parentView && (
                <div className="flex items-center gap-2" data-testid="streak-counter">
                  <div className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5 shadow-sm" title="Overall consecutive training days">
                    <Flame size={20} className="text-[#FF9100] streak-fire" />
                    <div className="text-right">
                      <span className="font-['Barlow_Condensed'] text-xl font-black text-[#FF9100] leading-none">
                        {user?.streak?.current || 0}
                      </span>
                      <p className="text-[8px] text-[#94A3B8] font-bold uppercase tracking-widest leading-none mt-0.5">Overall</p>
                    </div>
                  </div>
                  <div
                    className="flex items-center gap-2 bg-gradient-to-r from-[#FF9100]/10 to-[#FACC15]/10 border border-[#FF9100]/30 rounded-xl px-3 py-2.5 shadow-sm"
                    title="This week's scheduled days completed (resets Monday). Each day = +2 score bonus, max +10."
                    data-testid="weekly-streak-counter"
                  >
                    <div className="text-right">
                      <div>
                        <span className="font-['Barlow_Condensed'] text-xl font-black text-[#FF9100] leading-none">
                          {user?.streak?.weekly || 0}
                        </span>
                        <span className="text-[10px] text-[#94A3B8] font-bold ml-0.5">/5</span>
                      </div>
                      <p className="text-[8px] text-[#94A3B8] font-bold uppercase tracking-widest leading-none mt-0.5">This Wk</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {parentView ? (
            <ParentDashboard />
          ) : (
          <>
          {/* Training Week Calendar */}
          {plan?.days && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}
              className="bg-white border border-[#E2E8F0] rounded-xl p-4 shadow-sm"
              data-testid="streak-calendar"
            >
              <p className="text-[9px] text-[#94A3B8] font-bold uppercase tracking-widest mb-3">This Week</p>
              <div className="flex items-center justify-between">
                {['M','T','W','T','F','S','S'].map((label, i) => {
                  const dayNum = i + 1;
                  const dayData = plan.days.find(d => d.day_number === dayNum);
                  const dayDrills = dayData?.drills || [];
                  const allDone = dayDrills.length > 0 && dayDrills.every(dr => completedDrillIds.has(dr.id));
                  const isToday = dayData?.is_today;
                  const isWeekendDay = dayNum > 5;
                  const isPast = dayData?.unlocked && !isToday;

                  return (
                    <div key={i} className="flex flex-col items-center gap-1.5" data-testid={`cal-day-${i}`}>
                      <span className={`text-[10px] font-bold ${isToday ? 'text-[#2563EB]' : 'text-[#94A3B8]'}`}>{label}</span>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                        allDone
                          ? 'bg-[#10B981] text-white'
                          : isToday
                            ? 'bg-[#2563EB]/10 border-2 border-[#2563EB] text-[#2563EB]'
                            : isPast && !isWeekendDay
                              ? 'bg-[#FEE2E2] text-[#EF4444]'
                              : 'bg-[#F1F5F9] text-[#CBD5E1]'
                      }`}>
                        {allDone ? (
                          <CheckCircle2 size={18} strokeWidth={3} />
                        ) : isToday ? (
                          <span className="text-xs font-black">{new Date().getDate()}</span>
                        ) : (
                          <span className="text-xs font-bold">{isWeekendDay ? '—' : ''}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Welcome to Week 1 — Coach intro video (only Week 1 players) */}
          {plan?.player_week === 1 && !isWeekend && <WelcomeVideoCard />}

          {/* Weekly Challenge — pinned above the daily plan */}
          <WeeklyChallengeCard />

          {/* Today's Focus Card */}
          {todayDay && !isWeekend && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-[#0A2242] rounded-2xl p-5 text-white"
            >
              <div className="flex items-center gap-2 mb-2">
                <Flame size={14} className="text-[#FF9100]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#7B93B8]">
                  {isCatchUp ? 'Next Session · Catch Up' : "Today's Focus"}
                </span>
              </div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight">
                {todayDay.focus_area || todayDay.day_label || "Today's Training"}
              </h3>
              {isCatchUp && (() => {
                const totalToCatchUp = sortedDays.filter(d => d.unlocked && (d.drills || []).length > 0).length;
                const catchUpPosition = sortedDays.filter(d => d.unlocked && (d.drills || []).length > 0 && d.day_number <= todayDay.day_number).length;
                if (totalToCatchUp <= 1) return null;
                return (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${((catchUpPosition - 1) / totalToCatchUp) * 100}%` }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        className="h-full bg-[#FF9100] rounded-full"
                      />
                    </div>
                    <span className="text-[10px] font-bold text-[#FF9100] uppercase tracking-widest whitespace-nowrap">
                      Catching up: Day {catchUpPosition} of {totalToCatchUp}
                    </span>
                  </div>
                );
              })()}
              <button
                onClick={() => navigate(`/day/${todayDay.day_number}?week=${plan.week_number}`)}
                className="mt-4 bg-white text-[#0A2242] font-bold uppercase tracking-wide rounded-xl px-6 py-3 text-sm transition-colors hover:bg-[#F1F5F9] w-full"
                data-testid="start-session-btn"
              >
                Start Session
              </button>
            </motion.div>
          )}

          {/* Progress Overview */}
          {stats && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-3 gap-3">
              {[
                { label: 'Completed', value: stats.total_completed, color: 'text-[#10B981]' },
                { label: 'This Week', value: `${stats.week_completed}/${stats.week_total || '—'}`, color: 'text-[#2563EB]' },
                { label: 'Best Streak', value: stats.longest_streak, color: 'text-[#FF9100]' },
              ].map(({ label, value, color }, i) => (
                <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-center shadow-sm">
                  <span className={`font-['Barlow_Condensed'] text-2xl font-black ${color}`}>{value}</span>
                  <p className="text-[9px] text-[#94A3B8] font-bold uppercase tracking-widest mt-1">{label}</p>
                </div>
              ))}
            </motion.div>
          )}

          {/* Today's Score + Badges */}
          {(todayScore?.drills_done > 0 || badges.length > 0) && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="bg-white border border-[#E2E8F0] rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center justify-between mb-3">
                {todayScore?.drills_done > 0 && (
                  <div className="flex items-center gap-2">
                    <Star size={14} className="text-[#2563EB]" />
                    <span className="text-[10px] text-[#94A3B8] uppercase tracking-widest font-bold">Today's Score</span>
                    <span className="font-['Barlow_Condensed'] text-xl font-black text-[#2563EB]" data-testid="dashboard-today-score">
                      {todayScore.percentage}<span className="text-xs text-[#94A3B8]">/100</span>
                    </span>
                  </div>
                )}
              </div>
              {badges.length > 0 && (
                <div>
                  <p className="text-[9px] text-[#94A3B8] uppercase tracking-widest font-bold mb-2">Badges</p>
                  <div className="flex gap-3" data-testid="dashboard-badges">
                    {badges.map((badge) => (
                      <div key={badge.name} className="flex flex-col items-center gap-1">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center border-2"
                          style={{ borderColor: badge.color, background: `${badge.color}15` }}>
                          <Award size={20} style={{ color: badge.color }} />
                        </div>
                        <span className="text-[9px] font-bold" style={{ color: badge.color }}>{badge.name}</span>
                      </div>
                    ))}
                    {badges.length < 3 && (
                      <div className="flex flex-col items-center gap-1 opacity-30">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#E2E8F0] border-dashed">
                          <Award size={20} className="text-[#E2E8F0]" />
                        </div>
                        <span className="text-[9px] font-bold text-[#CBD5E1]">
                          {!badges.find(b => b.name === 'Bronze') ? 'Bronze' : !badges.find(b => b.name === 'Silver') ? 'Silver' : 'Gold'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Missed Days Alert */}
          {missedDays.length > 0 && !isWeekend && (
            <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              onClick={() => navigate('/progress')}
              className="w-full bg-[#FF9100]/10 border border-[#FF9100]/30 rounded-xl p-4 flex items-center gap-3 text-left"
              data-testid="missed-days-alert"
            >
              <AlertCircle size={20} className="text-[#FF9100] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-[#FF9100] font-semibold">
                  {missedDays.length} missed day{missedDays.length > 1 ? 's' : ''} this week
                </p>
                <p className="text-[10px] text-[#94A3B8]">Tap to catch up — you can still complete them</p>
              </div>
              <ChevronRight size={16} className="text-[#FF9100]" />
            </motion.button>
          )}

          {/* Today's Training */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-[#2563EB]" />
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-tight text-[#0A2242]">
                  {isWeekend ? 'WEEKEND CATCH-UP' : "TODAY'S TRAINING"}
                </h3>
              </div>
              {todayDay && (
                <span className="text-xs text-[#2563EB] font-bold">{todayDay.day_label}</span>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : !plan ? (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center shadow-sm">
                <p className="text-[#64748B] text-sm font-medium">Week {user?.current_week || 1} is being built.</p>
                <p className="text-[#94A3B8] text-xs mt-2">Your coach is preparing your next training plan. Check back soon!</p>
              </div>
            ) : isWeekend ? (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 text-center shadow-sm">
                <Calendar size={32} className="text-[#2563EB] mx-auto mb-3" />
                <h4 className="font-['Barlow_Condensed'] text-base font-bold mb-1 text-[#0A2242]">ENJOY YOUR WEEKEND</h4>
                <p className="text-sm text-[#64748B] mb-4">Missed any drills this week? You can catch up now.</p>
                <button
                  onClick={() => navigate('/progress')}
                  className="bg-[#0A2242] hover:bg-[#0E2D5A] text-white font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-colors text-sm"
                  data-testid="weekend-catchup-btn"
                >
                  View Weekly Plan
                </button>
              </div>
            ) : !todayDay ? (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center shadow-sm">
                <p className="text-[#94A3B8] text-sm">No drills scheduled for today.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(todayDay.drills || []).map((drill, i) => {
                  const isComplete = completedDrillIds.has(drill.id);
                  return (
                    <motion.button
                      key={drill.id}
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * i }}
                      onClick={() => navigate(`/drill/${drill.id}?week=${plan.week_number}&day=${todayDay.day_number}`)}
                      className={`w-full text-left bg-white border rounded-xl p-4 flex items-center gap-4 transition-all hover:border-[#2563EB] hover:-translate-y-0.5 hover:shadow-md ${
                        isComplete ? 'border-[#10B981]/40' : 'border-[#E2E8F0]'
                      } shadow-sm`}
                      data-testid={`drill-card-${i}`}
                    >
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isComplete ? 'bg-[#10B981]/10' : 'bg-[#2563EB]/10'
                      }`}>
                        {isComplete ? (
                          <CheckCircle2 size={24} className="text-[#10B981]" />
                        ) : (
                          <Play size={20} className="text-[#2563EB] ml-0.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight truncate text-[#0A2242]">{drill.title}</h4>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] text-[#94A3B8] font-bold uppercase tracking-wider">{drill.category}</span>
                          <span className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
                            <Clock size={10} /> {drill.duration_minutes}m
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-[#CBD5E1]" />
                    </motion.button>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Today's Tips */}
          {todayDay && (todayDay.mental_tip || todayDay.nutrition_tip) && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-3">
              {todayDay.mental_tip && (
                <div className="bg-[#A855F7]/5 border border-[#A855F7]/15 rounded-xl p-4" data-testid="mental-tip">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain size={14} className="text-[#A855F7]" />
                    <span className="text-[10px] text-[#A855F7] font-bold uppercase tracking-widest">Mental Prep</span>
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">{todayDay.mental_tip}</p>
                </div>
              )}
              {todayDay.nutrition_tip && (
                <div className="bg-[#10B981]/5 border border-[#10B981]/15 rounded-xl p-4" data-testid="nutrition-tip">
                  <div className="flex items-center gap-2 mb-2">
                    <Apple size={14} className="text-[#10B981]" />
                    <span className="text-[10px] text-[#10B981] font-bold uppercase tracking-widest">Nutrition Tip</span>
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">{todayDay.nutrition_tip}</p>
                </div>
              )}
            </motion.div>
          )}

          {/* View Full Plan CTA */}
          {plan && (
            <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              onClick={() => navigate('/progress')}
              className="w-full bg-white border-2 border-[#E2E8F0] hover:border-[#0A2242] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 shadow-sm"
              data-testid="view-plan-btn"
            >
              View Full Week Plan
              <ChevronRight size={16} />
            </motion.button>
          )}

          {/* Push Notifications */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <NotificationToggle />
          </motion.div>
          </>
          )}
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
